import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getCollection, optimizeImage } from '../lib/cms';

export default function Home() {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    async function loadData() {
      const [svcData, projData] = await Promise.all([
        getCollection('services'),
        getCollection('projects'),
      ]);
      setServices(svcData || []);
      setProjects(projData || []);
    }
    loadData();
  }, []);

  const competenceCards = [
    {
      title: 'Mekanisk konstruktion',
      desc: 'Udvikling og konstruktion af maskiner og maskindele, freelance konstruktion, 2D/3D tegning i Inventor, NX og Creo samt FEM analyse.',
      icon: 'fa-solid fa-gears',
      link: '/ydelser/mekanisk-konstruktion',
      tag: 'Inventor • NX • Creo',
    },
    {
      title: 'Elkonstruktion & Tavler',
      desc: 'Konstruktion og opbygning af styretavler efter gældende standarder, eldiagrammer i PC Schematik samt CE mærkning.',
      icon: 'fa-solid fa-bolt',
      link: '/ydelser/elkonstruktion',
      tag: 'PC Schematik • CE',
    },
    {
      title: 'Automation & Robotter',
      desc: 'Programmering af KUKA, Motoman og UR (Universal Robots) samt PLC (Siemens, Mitsubishi, Allan Bradley) og SCADA.',
      icon: 'fa-solid fa-robot',
      link: '/automation',
      tag: 'UR • KUKA • Siemens',
    },
    {
      title: 'Automatiske maskiner',
      desc: 'Udvikling og levering af specialmaskiner og automatiske procesceller skræddersyet til kundens produktionsbehov.',
      icon: 'fa-solid fa-industry',
      link: '/automation/automatiske-maskiner',
      tag: 'Specialanlæg • Montage',
    },
    {
      title: 'Produktionsudstyr & Værktøj',
      desc: 'Specialudstyr, produktionshjælpemidler, montagefiksturer og mekaniske tilpasninger til optimeret produktionsflow.',
      icon: 'fa-solid fa-wrench',
      link: '/produktionsudstyr',
      tag: 'Fiksturer • Udstyr',
    },
    {
      title: 'Problemløsninger',
      desc: 'Fejlretning, procesoptimering, afhjælpning af flaskehalse og automatisering af tidskrævende manuelle opgaver.',
      icon: 'fa-solid fa-screwdriver-wrench',
      link: '/problemlosninger',
      tag: 'Optimering • Retrofit',
    },
  ];

  const processSteps = [
    { num: '01', title: 'Behov & Idé', desc: 'Indledende teknisk dialog, analyse af maskinbehov og specifikation.' },
    { num: '02', title: 'Konstruktion & CAD', desc: '3D modellering i Inventor/NX, tolerancer og FEM-styrkeberegning.' },
    { num: '03', title: 'El & Tavlebygning', desc: 'Eldesign, diagrammer i PC Schematik og bygning af styretavler.' },
    { num: '04', title: 'Automation & Kode', desc: 'PLC- og robotprogrammering med fokus på stabil drift og sikkerhed.' },
    { num: '05', title: 'Montage & Indkøring', desc: 'Mekanisk samling, ledningsføring og funktionstest på værksted/site.' },
    { num: '06', title: 'Test & CE-mærkning', desc: 'Verifikation, risikovurdering og fuld teknisk dokumentation.' },
  ];

  return (
    <div className="bg-[#0b1017] text-slate-100 min-h-screen">
      <SEO
        title="JARCon ApS - Mekatronik, Konstruktion & Automation"
        description="JARCon ApS er en Mekatronik ingeniør og konsulentvirksomhed, som tilbyder Konsulentydelser, konstruktion og problemløsning indenfor produktionsudstyr og produkter."
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center border-b border-slate-800 technical-grid overflow-hidden -mt-20 min-[1200px]:-mt-[112px] pt-28 sm:pt-32 min-[1200px]:pt-40 pb-20 lg:pb-28">
        {/* Clean solid/gradient base background until customer image is provided */}
        <div className="absolute inset-0 z-0 bg-[#080d14]">
          {/* Subtle industrial blueprint grid and lighting accents */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(14,165,233,0.15),rgba(255,255,255,0))]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c131d] via-[#080d14] to-[#080d14]" />
          <div className="absolute -top-40 right-0 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-1/2 -left-20 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-3xl space-y-6">
            {/* Status indicator badge */}
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-md bg-slate-900/90 border border-slate-700/80 text-xs font-open-sans text-slate-300">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-cyan-400 font-semibold">JARCon ApS</span>
              <span className="text-slate-600">|</span>
              <span>Mekatronik Ingeniør &amp; Konsulent</span>
            </div>

            {/* H1 Headline */}
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1]">
              Tekniske løsninger fra idé til færdig maskine
            </h1>

            {/* Verbatim Subtext */}
            <p className="text-base sm:text-xl text-slate-300 font-sans leading-relaxed">
              JARCon ApS er en Mekatronik ingeniør og konsulentvirksomhed, som tilbyder Konsulentydelser, konstruktion og problemløsning indenfor produktionsudstyr og produkter.
            </p>

            {/* CTA Buttons */}
            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold text-base tracking-wide transition-all shadow-xl shadow-cyan-950/60 hover:shadow-cyan-500/25 active:scale-[0.98]"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <Link
                to="/komplette-losninger"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-slate-900/90 hover:bg-slate-800 text-slate-200 border border-slate-700 hover:border-cyan-500/60 font-semibold text-base transition-all"
              >
                <span>Se vores løsninger</span>
                <i className="fa-solid fa-chevron-right text-xs text-slate-400" aria-hidden="true" />
              </Link>
            </div>

            {/* Quick Metrics / Technical Highlights */}
            <div className="pt-8 border-t border-slate-800/80 grid grid-cols-2 sm:grid-cols-3 gap-6 font-open-sans text-xs text-slate-400">
              <div>
                <span className="text-cyan-400 text-lg font-bold block text-white font-display">Mekatronik</span>
                <span>Mekanik + El + Software</span>
              </div>
              <div>
                <span className="text-cyan-400 text-lg font-bold block text-white font-display">3D CAD &amp; FEM</span>
                <span>Inventor • NX • Creo</span>
              </div>
              <div>
                <span className="text-cyan-400 text-lg font-bold block text-white font-display">Alsidighed</span>
                <span>Fra idé til CE-mærkning</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Competence Areas Grid */}
      <section className="py-20 border-b border-slate-800 bg-[#090e15] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest block mb-2 font-semibold">
                Tekniske kernekompetencer
              </span>
              <h2 className="font-display text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                Specialiseret ingeniørarbejde &amp; maskinbygning
              </h2>
              <p className="mt-3 text-slate-300 text-sm sm:text-base leading-relaxed">
                Kombinationen af en praktisk smedeuddannelse og en mekatronik diplomingeniøruddannelse giver et sjældent stærkt grundlag for gennemtænkte løsninger, der fungerer både i teorien og på værkstedsgulvet.
              </p>
            </div>
            <Link
              to="/konsulentydelser"
              className="text-cyan-400 hover:text-cyan-300 font-open-sans text-xs uppercase tracking-wider inline-flex items-center gap-2 shrink-0 group"
            >
              <span>Udforsk alle ydelser</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {competenceCards.map((card) => (
              <Link
                key={card.title}
                to={card.link}
                className="group flex flex-col justify-between p-6 sm:p-8 rounded-xl bg-[#0e1622] border border-slate-800/80 hover:border-cyan-500/60 hover:shadow-2xl hover:shadow-cyan-950/20 transition-all duration-200"
              >
                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-lg bg-cyan-950/70 border border-cyan-500/30 text-cyan-400 flex items-center justify-center text-xl group-hover:bg-cyan-500 group-hover:text-white transition-colors">
                      <i className={card.icon} aria-hidden="true" />
                    </div>
                    <span className="text-[11px] font-open-sans text-slate-400 bg-slate-900 px-2.5 py-1 rounded border border-slate-800">
                      {card.tag}
                    </span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-white group-hover:text-cyan-400 transition-colors mb-2.5">
                    {card.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed font-sans">
                    {card.desc}
                  </p>
                </div>
                <div className="pt-6 mt-6 border-t border-slate-800/80 flex items-center justify-between text-xs font-open-sans text-cyan-400 font-semibold">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process: Sådan arbejder vi */}
      <section className="py-20 border-b border-slate-800 bg-[#0a0f16]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest block mb-2 font-semibold">
              Gennemtestet proces
            </span>
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              Sådan arbejder vi – Fra specifikation til idriftsættelse
            </h2>
            <p className="mt-3 text-slate-300 text-sm sm:text-base leading-relaxed">
              En struktureret mekatronisk arbejdsgang minimerer grænsefladeproblemer mellem mekanik, elektronik og software.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step) => (
              <div
                key={step.num}
                className="p-6 rounded-xl bg-[#0e1622] border border-slate-800 relative overflow-hidden"
              >
                <div className="text-4xl font-display font-extrabold text-slate-500/80 mb-3 select-none">
                  {step.num}
                </div>
                <h3 className="font-display text-lg font-bold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected References Logos & Projects Preview */}
      <section className="py-20 border-b border-slate-800 bg-[#070b10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest block mb-2 font-semibold">
                Tillid og samarbejde
              </span>
              <h2 className="font-display text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                Udvalgte referencer
              </h2>
            </div>
            <Link
              to="/projekter"
              className="text-cyan-400 hover:text-cyan-300 font-open-sans text-xs uppercase tracking-wider inline-flex items-center gap-2 group"
            >
              <span>Se alle referencer og cases</span>
              <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>

          {/* Partner logos grid with all 7 references from jarcon.dk/referencer */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/euc-logo.jpg"
                  alt="EUC-SYD"
                  className="max-h-12 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">EUC-SYD</span>
              <span className="text-[11px] text-slate-400 mt-1">Automationsudstyr til skolebrug</span>
            </div>

            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/frpetersen-600-150x150.jpg"
                  alt="Fr. Petersen Maskinfabrik A/S"
                  className="max-h-12 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">Fr. Petersen Maskinfabrik</span>
              <span className="text-[11px] text-slate-400 mt-1">Maskinkonstruktion &amp; montage</span>
            </div>

            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/logo-e1444055127362-1.png"
                  alt="Holtec Automatic"
                  className="max-h-10 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">Holtec Automatic</span>
              <span className="text-[11px] text-slate-400 mt-1">Styretavler &amp; elkonstruktion</span>
            </div>

            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-01b2b211d5ea4244916831dd44ab4a7b-mv2.jpg"
                  alt="Gibotech"
                  className="max-h-11 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">Gibotech</span>
              <span className="text-[11px] text-slate-400 mt-1">Robotanlæg &amp; specialmaskiner</span>
            </div>

            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-e4773b90d5d84e7d9a79c755c2e3476b-mv2.jpg"
                  alt="EasyRobotics"
                  className="max-h-10 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">EasyRobotics</span>
              <span className="text-[11px] text-slate-400 mt-1">Mobile cobot- &amp; robotplatforme</span>
            </div>

            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/linak.jpg"
                  alt="LINAK"
                  className="max-h-10 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">LINAK A/S</span>
              <span className="text-[11px] text-slate-400 mt-1">Produktionsudstyr &amp; fiksturer</span>
            </div>

            <div className="p-5 rounded-xl bg-[#0d1420] border border-slate-800 flex flex-col items-center justify-between text-center hover:border-cyan-500/40 transition-colors group col-span-2 sm:col-span-1 lg:col-span-2">
              <div className="w-full h-16 bg-white rounded-lg p-2 flex items-center justify-center mb-3">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-cff9eab60c19441fbcd0a9e6e378026e-mv2.jpg"
                  alt="CK-Mt ApS"
                  className="max-h-12 max-w-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">CK-Mt ApS (CK Maskinteknik)</span>
              <span className="text-[11px] text-slate-400 mt-1">Laserskæring, CNC &amp; maskinbearbejdning</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Jens Christensen snippet on home */}
      <section className="py-20 bg-[#070b10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 max-w-sm mx-auto shadow-2xl">
                <img
                  src="https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-72949a8cb38c4c6caf56fddf60204a98-mv2.jpg"
                  alt="Jens Asmus Ritz Christensen, JARCon ApS"
                  className="w-full h-auto object-cover filter contrast-105"
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-4 text-center">
                  <span className="text-sm font-display font-bold text-white block">Jens Asmus Ritz Christensen</span>
                  <span className="text-xs font-open-sans text-cyan-400">Mekatronik Diplomingeniør &amp; Smed</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 space-y-4">
              <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest font-semibold block">
                Mød grundlæggeren
              </span>
              <h2 className="font-display text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                Alsidig praktisk erfaring kombineret med ingeniøruddannelse
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                &ldquo;Jeg er uddannet Mekatronik diplomingeniør, men oprindeligt er jeg uddannet smed i 1986. Herefter arbejdede jeg mange år i maskinbranchen med konstruktion, montage, indkøring, tavlebygning, programmering af PLC og robotter, samt nogle år som vedligeholder i en stor produktionsvirksomhed.&rdquo;
              </p>
              <p className="text-slate-400 text-sm leading-relaxed">
                &ldquo;Jeg er meget vedholdende af natur og slipper ikke en opgave, inden den er løst til alles tilfredshed. Min vision med virksomheden er at fremstå som en tillidsvækkende samarbejdspartner, som bidrager med ideer og løsninger der skaber værdi for mine kunder.&rdquo;
              </p>
              <div className="pt-2">
                <Link
                  to="/om-mig"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-open-sans text-xs uppercase tracking-wider font-semibold"
                >
                  <span>Læs hele historien om JARCon</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
